import { useMemo } from 'react';
import { useProductService, type TaxonomyTerm } from '@skin-seoul/service';
import { getCategoryOrder } from '../constants/categoryOrder';
import { decodeHTMLEntities } from '@skin-seoul/utils';

export interface TaxonomyCategory {
  termId: number;
  name: string;
  slug: string;
  thumbnailImageUrl?: string;
  subCategories?: TaxonomyCategory[];
}

export const buildCategoryHierarchy = (flatCategories: TaxonomyTerm[]): TaxonomyCategory[] => {
  const filtered = flatCategories.filter((cat) => !['#N/A'].includes(cat.name));

  const parents = filtered.filter((cat) => !cat.parentTermId);
  const childrenWithParents = filtered.filter((cat) => cat.parentTermId);

  const buildSubCategories = (parentId: number): TaxonomyCategory[] => {
    return childrenWithParents
      .filter((child) => child.parentTermId === parentId)
      .map((child) => ({
        termId: child.termId,
        name: decodeHTMLEntities(child.name),
        slug: child.slug,
        thumbnailImageUrl: child.thumbnailImageUrl,
        subCategories: buildSubCategories(child.termId),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  return parents
    .map((parent) => ({
      termId: parent.termId,
      name: decodeHTMLEntities(parent.name),
      slug: parent.slug,
      thumbnailImageUrl: parent.thumbnailImageUrl,
      subCategories: buildSubCategories(parent.termId),
    }))
    .sort((a, b) => {
      const orderA = getCategoryOrder(a.name);
      const orderB = getCategoryOrder(b.name);

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return a.name.localeCompare(b.name);
    });
};

export const useDynamicCategories = () => {
  const productService = useProductService();
  const { data: categoryTaxonomy } = productService.useGetCategoryTaxonomy();

  const categories = useMemo(() => {
    return categoryTaxonomy ? buildCategoryHierarchy(categoryTaxonomy) : [];
  }, [categoryTaxonomy]);

  return { categories };
};

const findCategoryBy = (
  categories: TaxonomyCategory[],
  predicate: (category: TaxonomyCategory) => boolean,
): TaxonomyCategory | undefined => {
  for (const category of categories) {
    if (predicate(category)) {
      return category;
    }
    if (category.subCategories?.length) {
      const found = findCategoryBy(category.subCategories, predicate);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

export const findCategoryBySlug = (categories: TaxonomyCategory[], slug: string): TaxonomyCategory | undefined => {
  return findCategoryBy(categories, (category) => category.slug === slug);
};

export const findCategoryByTermId = (categories: TaxonomyCategory[], termId: number): TaxonomyCategory | undefined => {
  return findCategoryBy(categories, (category) => category.termId === termId);
};

export const findTermIdBySlug = (categories: TaxonomyCategory[], slug: string): number | undefined => {
  return findCategoryBySlug(categories, slug)?.termId;
};

export const findNameByTermId = (categories: TaxonomyCategory[], termId: number): string | undefined => {
  return findCategoryByTermId(categories, termId)?.name;
};

export const findSlugByTermId = (categories: TaxonomyCategory[], termId: number): string | undefined => {
  return findCategoryByTermId(categories, termId)?.slug;
};

export const findParentCategory = (categories: TaxonomyCategory[], termId: number): TaxonomyCategory | undefined => {
  for (const category of categories) {
    if (category.subCategories?.some((sub) => sub.termId === termId)) {
      return category;
    }

    if (category.subCategories?.length) {
      const found = findParentCategory(category.subCategories, termId);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

/**
 * Find the complete hierarchy path for a given termId
 * Returns an array of categories from root to the target category
 */
export const findCategoryHierarchy = (
  categories: TaxonomyCategory[],
  termId: number,
  path: TaxonomyCategory[] = [],
): TaxonomyCategory[] => {
  for (const category of categories) {
    const currentPath = [...path, category];

    if (category.termId === termId) {
      return currentPath;
    }

    if (category.subCategories?.length) {
      const found = findCategoryHierarchy(category.subCategories, termId, currentPath);
      if (found.length > 0) {
        return found;
      }
    }
  }
  return [];
};

/**
 * Find the main category that contains a specific subcategory
 * @param categories - Array of taxonomy categories to search in
 * @param subcategoryTermId - The termId of the subcategory
 * @returns The termId of the main category, or null if not found
 */
export const findMainCategoryForSubcategory = (
  categories: TaxonomyCategory[],
  subcategoryTermId: number,
): number | null => {
  const parentCategory = categories.find((cat) => cat.subCategories?.some((sub) => sub.termId === subcategoryTermId));
  return parentCategory?.termId || null;
};

/**
 * Find the main category that contains a specific sub-subcategory
 * @param categories - Array of taxonomy categories to search in
 * @param subSubcategoryTermId - The termId of the sub-subcategory
 * @returns The termId of the main category, or null if not found
 */
export const findMainCategoryForSubSubcategory = (
  categories: TaxonomyCategory[],
  subSubcategoryTermId: number,
): number | null => {
  const main = categories.find((mainCategory) =>
    mainCategory.subCategories?.some((subCategory) =>
      subCategory.subCategories?.some((subSubCat) => subSubCat.termId === subSubcategoryTermId),
    ),
  );
  return main?.termId ?? null;
};

/**
 * Get sub-subcategories for a given subcategory
 * @param categories - Array of taxonomy categories to search in
 * @param subcategoryTermId - The termId of the subcategory
 * @returns Array of sub-subcategories, or empty array if none found
 */
export const getSubSubcategoriesForSubcategory = (
  categories: TaxonomyCategory[],
  subcategoryTermId: number,
): TaxonomyCategory[] => {
  // Find the subcategory in the hierarchy
  const subcategory = findCategoryByTermId(categories, subcategoryTermId);

  if (!subcategory || !subcategory.subCategories) {
    return [];
  }

  return subcategory.subCategories;
};

/**
 * Find the parent subcategory for a given sub-subcategory
 * @param categories - Array of taxonomy categories to search in
 * @param subSubcategoryTermId - The termId of the sub-subcategory
 * @returns The parent subcategory, or null if not found
 */
export const findParentSubcategoryForSubSubcategory = (
  categories: TaxonomyCategory[],
  subSubcategoryTermId: number,
): TaxonomyCategory | null => {
  for (const category of categories) {
    if (!category.subCategories) {
      continue;
    }

    const parentSubcategory = category.subCategories.find((sub) =>
      sub.subCategories?.some((subSub) => subSub.termId === subSubcategoryTermId),
    );

    if (parentSubcategory) {
      return parentSubcategory;
    }
  }
  return null;
};

/**
 * Generate URL path for a category based on its hierarchy
 * @param categories - Array of taxonomy categories to search in
 * @param termId - The termId of the target category
 * @returns URL path string (e.g., '/collections/moisturizer/cream/eye-cream/')
 */
export const generateCategoryUrl = (categories: TaxonomyCategory[], termId: number): string => {
  const hierarchy = findCategoryHierarchy(categories, termId);

  if (hierarchy.length === 0) {
    return '/collections/';
  }

  const slugs = hierarchy.map((cat) => cat.slug);
  return `/collections/${slugs.join('/')}/`;
};
